import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface FeeResponse {
  id: UUID;
  company_id: UUID;
  fee_type: string;
  amount: string;
  description: string | null;
  reference_type: string;
  reference_id: UUID;
  fee_metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface FeeCreate {
  fee_type: string;
  amount: string;
  description?: string | null;
  reference_type: string;
  reference_id: UUID;
  fee_metadata?: Record<string, unknown>;
}

export interface FeeUpdate {
  fee_type?: string;
  amount?: string;
  description?: string | null;
  fee_metadata?: Record<string, unknown>;
}

export interface FeeFilters {
  reference_type?: string;
  reference_id?: UUID;
  fee_type?: string;
}

export default class APIFees extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  list = async (filters?: FeeFilters): Promise<FeeResponse[]> => {
    const params = new URLSearchParams();
    if (filters?.reference_type)
      params.append("reference_type", filters.reference_type);
    if (filters?.reference_id)
      params.append("reference_id", filters.reference_id);
    if (filters?.fee_type) params.append("fee_type", filters.fee_type);

    const query = params.toString() ? `?${params.toString()}` : "";
    const res = await this._fetch({
      url: `/fees/${query}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  get = async (id: UUID): Promise<FeeResponse> => {
    const res = await this._fetch({
      url: `/fees/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getByReference = async (
    referenceType: string,
    referenceId: UUID,
  ): Promise<FeeResponse[]> => {
    const params = new URLSearchParams();
    params.append("reference_type", referenceType);
    params.append("reference_id", referenceId);

    const res = await this._fetch({
      url: `/fees/by-reference?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  getTotalByReference = async (
    referenceType: string,
    referenceId: UUID,
  ): Promise<{ total: string }> => {
    const params = new URLSearchParams();
    params.append("reference_type", referenceType);
    params.append("reference_id", referenceId);

    const res = await this._fetch({
      url: `/fees/total/by-reference?${params.toString()}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  create = async (data: FeeCreate): Promise<FeeResponse> => {
    const res = await this._fetch({
      url: `/fees/`,
      method: "POST",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  update = async (id: UUID, data: FeeUpdate): Promise<FeeResponse> => {
    const res = await this._fetch({
      url: `/fees/${id}`,
      method: "PATCH",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  delete = async (id: UUID): Promise<void> => {
    await this._fetch({
      url: `/fees/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  deleteByReference = async (
    referenceType: string,
    referenceId: UUID,
  ): Promise<{ deleted_count: number }> => {
    const params = new URLSearchParams();
    params.append("reference_type", referenceType);
    params.append("reference_id", referenceId);

    const res = await this._fetch({
      url: `/fees/by-reference?${params.toString()}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json")) {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
